import { PersonWalkingDashedLineArrowRightRegularIcon } from "@elixir/icons";
import { useCreateAptStore } from "@elixir/stores";
import {
  Appointment,
  APPOINTMENT_CHANNEL,
  QUEUE_TYPE,
  RECEIPT_PAYMENT_MODE_ID,
  SUBJECT_KEYS,
  SupportedTeleProviders,
  TagRecord,
  TSelectedSkuItem,
} from "@elixir/types";
import {
  getAclViewClassName,
  getDocIdFromClinicIdForApt,
  getMedicalHistory,
  isSameDay,
} from "@elixir/utils";
import { notification, Popover, Spin } from "antd";
import React, { useMemo } from "react";
import {
  NewAptSelectMedicalHistoryUi,
  PrimaryButton,
  SecondaryButton,
} from "ui";
import { v4 as uuidV4 } from "uuid";
import useGetPatientByPid from "../../../apis/parchi/use-get-patient-by-pid";
import useGetPatientHistory from "../../../apis/parchi/use-get-patient-history";
import usePostCreateReceipt from "../../../apis/tryst/create-receipt";
import usePostBookSlot from "../../../apis/tryst/post-book-slot";
import usePostEndVisit from "../../../apis/tryst/post-end-visit";
import { ReactComponent as CalendarSolidIcon } from "../../../assets/icons/font-awesome/calendar-solid.svg";
import { MH_POPUP_CONTAINER_ID } from "../../../constants/config";
import useQueueStore from "../../../stores/queue-store";
import {
  APPOINTMENT_STATUS,
  MODES_OF_APT,
} from "../../../stores/queue-store/enums";
import { getReceiptObjectForRequest } from "../../../utils/billing-utils";
import { serializeErrorForTracking, track } from "../../../utils/tracking";
import RequestedAppointmentDropdown from "../requested-appointment";
import NewAbhaStatusPatient from "./new-abha-status-patient";
import NewAptExistingAppointment from "./new-apt-existing-appointment";
import NewAptSelectExistingPackage from "./new-apt-select-existing-package";
import NewAptSelectedPatient, {
  FREE_LINE_ID,
} from "./new-apt-selected-patient";
import NewAptSelectedServices from "./new-apt-selected-services";
import NewAptSelectedTags from "./new-apt-selected-tags";
import NewAptSelectedTime from "./new-apt-selected-time";
import dayjs from "dayjs";
import NewAptSelectDoctor from "./select-doctor";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";
import { navigateLocal } from "../queue-header";
import getBridges from "../../../utils/bridges/get-bridges";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";
import SelectTelePlatform from "./select-tele-platform";
import { getBookedAptChannel } from "../../../utils/appointment-utils";
import NewAptSelectClinic from "./select-clinic";
import useCanShowOnboardingChange from "../../../hooks/use-can-show-onboarding-change";
import {
  isTreatmentPlanEnabled,
  getCreateApptConfig,
  CreateApptConfigItem,
  isAccessAvailable,
} from "../../../utils/bridges/methods";
import { useReceiptStore } from "@elixir/stores";
import {
  generateReceiptPDFOnClient,
  PAGIFY_FAILURE_FALLBACK_STRING,
  ReceiptObjectForPdf,
} from "../../../receiptOnClient/GeneratePDFOnClient";
import uploadReceiptBlobV2 from "../../../apis/parchi/put-receipt-blob-v2";
import { massagePatientProfileForReceipt } from "../../../apis/tryst/receipt-v2/use-massage-receipt-data-and-config-for-pdf";
import { ReceiptV2ReturnResponseMassaged } from "../../billing-queue/receipt-actions";
import { docListMassaged } from "../../receipt-customisation/customisation-utls";
import { makeReceiptOnLocal } from "../../../apis/tryst/receipt-v2/should-receipt-be-made-on-local-util";
import useCreateReceiptPdfV2 from "../../../apis/tryst/create-receipt-pdf-v2-handler";
import useReactXarrows from "../../../hooks/use-react-xarrows";
import ApptConfigEditModal from "./appt-config-edit-modal";
import useCanShowOnboardingArrows from "../../../hooks/use-queue-apts-count";
import { getCustomSlots } from "../../../apis/tryst/get-custom-slots";

export enum APPOINTMENT_PAYMENT_STATUS_V1 {
  PAID = "PAID",
  UNPAID = "UNPAID",
  PARTIALLY_PAID = "PARTIALLY_PAID",
  REFUNDED = "REFUNDED",
  FAILED = "FAILED",
}

export const appointmentPaymentStatusItems = [
  { label: "Paid", value: APPOINTMENT_PAYMENT_STATUS_V1.PAID },
  { label: "Unpaid", value: APPOINTMENT_PAYMENT_STATUS_V1.UNPAID },
  {
    label: "Partially Paid",
    value: APPOINTMENT_PAYMENT_STATUS_V1.PARTIALLY_PAID,
  },
];

export const CHECK_IN_TODAYS_FIRST_PATIENT_ID = "check-in-todays-first-patient";

const AddNewPatient = () => {
  const selectedSlot = useQueueStore((state) => state.selectedSlot);
  const clearSelectedSlot = useQueueStore((state) => state.clearSelectedSlot);
  const setLeftPanelQueueType = useQueueStore(
    (state) => state.setLeftPanelQueueType
  );
  const setOpenLeftPanel = useQueueStore((state) => state.setOpenLeftPanel);
  const setClinicId = useQueueStore((state) => state.setClinicId);
  const setShowSelectSlot = useQueueStore((state) => state.setShowSelectSlot);

  const clinicId = useCreateAptStore((state) => state.selectedClinic);

  const setScrollToValues = useQueueStore((state) => state.setScrollToValues);

  const setVcMeta = useQueueStore((state) => state.setVcMeta);

  const selectedInclusionIds = useCreateAptStore(
    (state) => state.selectedInclusionIds
  );
  const setSelectedInclusionIds = useCreateAptStore(
    (state) => state.setSelectedInclusionIds
  );
  const selectedDoctor = useCreateAptStore((state) => state.selectedDoctor);

  const { data: myAccessProfile } = useMyAccessProfileV2();

  const { data: queueConfigData } = useGetQueueConfig();

  const [selectedServiceSkus, setSelectedServiceSkus] = React.useState<
    TSelectedSkuItem[]
  >([]);

  const customSlotDuration = useQueueStore((state) => state.customSlotDuration);

  const setSelectedDate = useQueueStore((state) => state.setSelectedDate);

  const selectedPatient = useQueueStore((state) => state.selectedPatient);
  const setSelectedPatient = useQueueStore((state) => state.setSelectedPatient);
  const mode = useQueueStore((state) => state.mode);

  const setReceipt = useReceiptStore((state) => state.setReceipt);
  const receipt = useReceiptStore((state) => state.receipt);

  const [selectedTags, setSelectedTags] = React.useState<
    Array<TagRecord["tag_id"]>
  >([]);
  const handleTagClick = (selectedTagId: TagRecord["tag_id"]) => {
    const selectedIndex = selectedTags.findIndex(
      (tag) => tag === selectedTagId
    );

    if (selectedIndex < 0) {
      setSelectedTags([...selectedTags, selectedTagId]);
    } else {
      setSelectedTags((currTags) => {
        return [
          ...currTags.slice(0, selectedIndex),
          ...currTags.slice(selectedIndex + 1),
        ];
      });
    }
  };

  const [selectedPaymode, setSelectedPaymode] =
    React.useState<RECEIPT_PAYMENT_MODE_ID>(RECEIPT_PAYMENT_MODE_ID.CASH);

  const [selectedPaymentStatus, setSelectedPaymentStatus] = React.useState<
    APPOINTMENT_PAYMENT_STATUS_V1 | undefined
  >(undefined);

  const [createApptConfig, setCreateApptConfig] = React.useState<
    CreateApptConfigItem[] | null
  >(null);

  const [showConfigModal, setShowConfigModal] = React.useState(false);

  React.useEffect(() => {
    const loadConfig = async () => {
      const config = await getCreateApptConfig();
      setCreateApptConfig(config);
    };
    loadConfig();
  }, []);

  const onAdvanceSettingsClick = () => {
    setShowConfigModal(true);
    track("ADVANCE_SETTINGS_CLICKED");
  };

  const handleConfigSave = (config: CreateApptConfigItem[]) => {
    setCreateApptConfig(config);
  };

  const isPaymentStatusMode = React.useMemo(() => {
    if (!createApptConfig) return false;
    const associatePaymentsConfig = createApptConfig.find(
      (c) => c.id === "associate_payments_to_appointments"
    );
    return !!associatePaymentsConfig?.isShown;
  }, [createApptConfig]);

  const isQuickReceiptMode = React.useMemo(() => {
    if (!createApptConfig) return false;
    const quickReceiptConfig = createApptConfig.find(
      (c) => c.id === "auto_create_receipt_on_services"
    );
    const isCreateReceiptAllowed = isAccessAvailable(
      SUBJECT_KEYS.CAN_CREATE_RECEIPT_WITH_APT
    );
    return !!quickReceiptConfig?.isShown && isCreateReceiptAllowed;
  }, [createApptConfig]);

  const { mutateAsync: postBookSlot, isLoading: isPostBookSlotLoading } =
    usePostBookSlot();

  const { mutateAsync: postEndVisit, isLoading: isPostEndVisitLoading } =
    usePostEndVisit();

  const { mutateAsync: createReceiptPdfServer } = useCreateReceiptPdfV2();

  const {
    mutateAsync: postCreateReceipt,
    isLoading: isPostCreateReceiptLoading,
  } = usePostCreateReceipt();

  const setRightPanelQueueType = useQueueStore(
    (state) => state.setRightPanelQueueType
  );

  const businessId = myAccessProfile?.business?.id ?? "";

  const [activeButton, setActiveButton] = React.useState<
    "book_and_checkin" | "book" | "checkin" | ""
  >("");

  const [isBookAppointmentProcessing, setIsBookAppointmentProcessing] =
    React.useState(false);

  const { showArrows, ReactXarrowsWrapper } = useReactXarrows();
  const canShowOnboardingChange = useCanShowOnboardingChange();
  const canShowOnboardingArrows = useCanShowOnboardingArrows();

  React.useEffect(() => {
    if (activeButton) {
      setTimeout(() => {
        setActiveButton("");
      }, 5000);
    }
  }, [activeButton]);

  const isSelectedDayToday = React.useMemo(() => {
    if (!selectedSlot) return false;
    const { startTime } = selectedSlot;
    return isSameDay(new Date(startTime), new Date());
  }, [selectedSlot]);

  const isSlotDateFromPast = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedSlot) {
      const { startTime } = selectedSlot;
      const startTimeObj = new Date(startTime);
      if (startTimeObj.getTime() < today.getTime()) {
        return true;
      }
    }
    return false;
  }, [selectedSlot]);

  const usersListEntire = docListMassaged();

  const activeDoctorProfile =
    usersListEntire.doctors.find((d) => d.oid === selectedDoctor?.oid) ||
    myAccessProfile?.allDocs.find((d) => d.oid === selectedDoctor?.oid);

  const activerClinic =
    //@ts-ignore
    activeDoctorProfile?.clinics.find(
      (c) => (c as unknown as { id: string }).id == clinicId
    ) || myAccessProfile?.clinics.find((c) => c.id === clinicId);

  const handleCreateReceiptWithPdfAndUpload = async (
    createdReceiptResponse: ReceiptV2ReturnResponseMassaged,
    currentAppointment: Appointment
  ) => {
    try {
      const resOfPdf = await generateReceiptPDFOnClient({
        receiptData: createdReceiptResponse.data!,
        pdfConfigForGeneration:
          createdReceiptResponse?.meta?.pdfConfigForGeneration,
        patientData: massagePatientProfileForReceipt(
          selectedPatient!,
          currentAppointment!
        ),
        doctorProfile: {
          ...activeDoctorProfile!,
          headerText: createdReceiptResponse?.meta?.headerText,
          clinicObject: activerClinic,
        },
      });

      if (!resOfPdf.success) {
        track("RECEIPT_PDF_GENERATION_FAILED", {
          on: "appointment flow",
          what: JSON.stringify(resOfPdf),
        });

        // notification.error({
        //   message: "PDF generation failed",
        //   placement: "bottomRight",
        //   duration: 3,
        // });
        return;
      }

      if (resOfPdf.success) {
        const uploadToS3Res = await uploadReceiptBlobV2({
          file: resOfPdf.blob!,
          fileName: createdReceiptResponse.generatedFileName,
        });

        if (!uploadToS3Res?.success) {
          track("RECEIPT_PDF_S3_UPLOAD_FAILED", {
            on: "appointment flow",
            what: JSON.stringify(uploadToS3Res),
          });
          notification.error({
            message: "Uploading Receipt to server failed",
            placement: "bottomRight",
            duration: 3,
          });
          return;
        }

        if (uploadToS3Res?.success) {
          setReceipt({
            ...(receipt ?? {}),
            receipt_url: uploadToS3Res?.url,
          });
          return resOfPdf.blob;
        }
      }
    } catch (err: unknown) {
      if ((err as Error)?.message?.includes(PAGIFY_FAILURE_FALLBACK_STRING)) {
        track("ERROR", {
          what: serializeErrorForTracking(err as Error),
          aid: currentAppointment?.aid,
          rid: receipt?.receipt_id?.toString(),
          identifier: "APPOINTMENT_RECEIPT_PAGIFY_FAILED_RETRY_SERVER",
        });

        useReceiptStore.getState().setforceServerReceiptGeneration(true);

        try {
          const response = await createReceiptPdfServer({
            pdfObject: (err as unknown as { pdfObject: ReceiptObjectForPdf })
              ?.pdfObject,
          });

          if (response?.receipt_url) {
            setReceipt({
              ...(receipt ?? {}),
              receipt_url: response?.receipt_url,
            });
            notification.success({
              message: "Receipt Created Successfully",
              placement: "bottomRight",
              duration: 3,
            });
          } else {
            throw new Error("Server PDF generation failed");
          }

          useReceiptStore.getState().setforceServerReceiptGeneration(false);
          notification.success({
            message: "Receipt Created Successfully",
            placement: "bottomRight",
            duration: 3,
          });
        } catch (retryError: unknown) {
          track("ERROR", {
            receipt_id: createdReceiptResponse?.data?.receipt_id?.toString(),
            aid: currentAppointment?.aid,
            error: (retryError as Error)?.message,
            what: serializeErrorForTracking(retryError as Error),
            identifier: "APPOINTMENT_RECEIPT_SERVER_RETRY_FAILED",
          });

          useReceiptStore.getState().setforceServerReceiptGeneration(false);
          notification.error({
            message: "Failed to create receipt",
            description: "Please try again or contact support",
            placement: "bottomRight",
            duration: 3,
          });
        }
      } else {
        track("ERROR", {
          receipt_id: createdReceiptResponse?.data?.receipt_id?.toString(),
          aid: currentAppointment?.aid,
          what: serializeErrorForTracking(err as Error),
          identifier: "ADD_NEW_PT_RECEIPT_FAILURE_NON_PAGIFY",
        });
      }
    }
  };

  const handleEndAptVisit = async () => {
    if (selectedDoctor?.oid && clinicId && selectedPatient) {
      const aid = `PV-${uuidV4()}`;
      const { success } = await postEndVisit({
        aid,
        doctor: {
          oid: selectedDoctor?.oid,
          clinic: clinicId,
        },
        pid: { oid: selectedPatient.oid },
        status: APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION,
        visit: {
          st: selectedSlot?.startTime || "",
          et: selectedSlot?.endTime || "",
        },
      });
      if (success) {
        if (selectedServiceSkus.length > 0) {
          const receiptData = getReceiptObjectForRequest({
            clinicId: clinicId,
            selectedSkus: selectedServiceSkus,
            selectedPatient,
            aid,
            selectedPaymode: selectedPaymode,
          });
          const createdReceiptResponse = await postCreateReceipt({
            businessId,
            ...receiptData,
            doctor_oid: activeDoctorProfile?.oid,
          });

          const currentAppointment = { aid, patient: selectedPatient };
          const makeOnLocal = makeReceiptOnLocal();
          if (makeOnLocal) {
            if (!receiptData?.meta?.request_payment) {
              await handleCreateReceiptWithPdfAndUpload(
                createdReceiptResponse as ReceiptV2ReturnResponseMassaged,
                currentAppointment as Appointment
              );
            }
          }
        }

        setSelectedDate(
          selectedSlot?.startTime
            ? new Date(selectedSlot.startTime)
            : new Date()
        );
        setRightPanelQueueType(QUEUE_TYPE.COMPLETED);
        if (document.body.clientWidth < 1280) {
          setOpenLeftPanel(false);
        }
        setScrollToValues({
          aid,
          queueType: QUEUE_TYPE.COMPLETED,
        });

        setSelectedPatient(undefined);
        setSelectedPaymode(RECEIPT_PAYMENT_MODE_ID.CASH);
        setSelectedPaymentStatus(undefined);
        setSelectedTags([]);
        setSelectedServiceSkus([]);
        clearSelectedSlot();
      }
      setSelectedInclusionIds([]);
    }
  };

  const handleAddAppointmentClick = async ({
    shouldBookAndCheckin,
  }: {
    shouldBookAndCheckin?: boolean;
  } = {}) => {
    if (!clinicId) {
      return;
    }

    if (selectedPatient) {
      const action = shouldBookAndCheckin
        ? "book_and_check_in"
        : selectedSlot
          ? "book_appointment"
          : "checkin";

      if (action === "book_appointment" && isBookAppointmentProcessing) {
        return;
      }

      if (action === "book_appointment") {
        setIsBookAppointmentProcessing(true);
      }

      track(action, {});

      try {
        if (
          (action === "book_and_check_in" || action === "checkin") &&
          (!selectedDoctor || !selectedDoctor.oid)
        ) {
          notification.error({
            message: "Cannot check in clinic level appointments!",
            description: "Please book the appointments instead",
            duration: 3000,
          });
          return;
        }

        const isClinicApt = !selectedDoctor || !selectedDoctor.oid;

        const docIdInApt = isClinicApt
          ? getDocIdFromClinicIdForApt({ clinicId })
          : selectedDoctor.oid;

        const newDateObject = new Date();
        let channel = APPOINTMENT_CHANNEL.WALKIN;
        if (selectedSlot) {
          channel = getBookedAptChannel({ docIdInApt });
        }
        if (isSlotDateFromPast) {
          handleEndAptVisit();
          return;
        }

        const selectedClinicId = clinicId;
        if (selectedSlot) {
          try {
            const getSlots = await getCustomSlots(
              dayjs(selectedSlot?.startTime).format("YYYY-MM-DD"),
              businessId,
              customSlotDuration,
              selectedClinicId,
              docIdInApt,
              mode,
              false
            );

            const slots =
              getSlots.slotsByDate[
                dayjs(selectedSlot?.startTime).format("YYYY-MM-DD")
              ];

            const slot = slots?.find((s) =>
              dayjs(s.s).isSame(dayjs(selectedSlot?.startTime))
            );

            console.log("debugx: slot", {
              slots,
              startTime: dayjs(selectedSlot?.startTime).format("YYYY-MM-DD"),
              slot,
            });

            if (!slot?.available) {
              notification.error({
                message:
                  "This slot is not available. Please select a different slot.",
              });
              track("SLOT_OVERLAPPING");
              setShowSelectSlot(true);
              return;
            }
          } catch (error) {
            console.error(error);
            notification.error({
              message:
                "This slot is not available. Please select a different slot.",
            });
            return;
          }
        }

        //TODO - Handle package inclusions
        const { success, data } = await postBookSlot({
          channel,
          mode,
          clinicId: selectedClinicId,
          doctorId: docIdInApt,
          patient: {
            oid: selectedPatient.oid,
            allowMultiBooking:
              queueConfigData?.data?.apt_config?.allow_pt_multi_apt,
          },
          tags: selectedTags,
          startTime: selectedSlot
            ? selectedSlot.startTime
            : newDateObject.toISOString(),
          endTime: selectedSlot
            ? selectedSlot.endTime
            : newDateObject.toISOString(),
          action: selectedSlot
            ? shouldBookAndCheckin
              ? "BOOK_AND_CHECK_IN"
              : APPOINTMENT_STATUS.BOOKED
            : APPOINTMENT_STATUS.CHECKED_IN,
          vc_meta:
            mode === MODES_OF_APT.TELE
              ? useQueueStore.getState().vcMeta || {
                platform: SupportedTeleProviders.eka,
              }
              : undefined,
          packages: {
            inclusion_ids: selectedInclusionIds,
          },
          //@ts-expect-error
          services: selectedServiceSkus.map((s) => ({
            price: s?.price,
            service_id: s?.service_id,
            service_name: s?.service_name,
          })),
          ...(isPaymentStatusMode &&
            selectedPaymentStatus && {
            payment: {
              status: selectedPaymentStatus,
            },
          }),
        });

        if (
          isQuickReceiptMode &&
          success &&
          data?.aid &&
          selectedServiceSkus.length > 0
        ) {
          setTimeout(async () => {
            const { aid } = data;
            const createdReceiptResponse = (await postCreateReceipt({
              businessId,
              doctor_oid: activeDoctorProfile?.oid,
              ...getReceiptObjectForRequest({
                clinicId: clinicId,
                selectedSkus: selectedServiceSkus,
                selectedPatient,
                aid,
                selectedPaymode: selectedPaymode,
              }),
            })) as ReceiptV2ReturnResponseMassaged;

            const currentAppointment = { aid, patient: selectedPatient };
            const makeOnLocal = makeReceiptOnLocal();
            if (makeOnLocal) {
              await handleCreateReceiptWithPdfAndUpload(
                createdReceiptResponse,
                currentAppointment as Appointment
              );
            }
          }, 800);
        }

        if (success) {
          if (selectedSlot) {
            if (shouldBookAndCheckin) {
              setRightPanelQueueType(QUEUE_TYPE.CHECKED_IN);
            } else {
              setLeftPanelQueueType(QUEUE_TYPE.BOOKED);
            }
          } else {
            setRightPanelQueueType(QUEUE_TYPE.CHECKED_IN);
            // if small screen then only close the left panel automaticaly
            if (document.body.clientWidth < 1280) {
              setOpenLeftPanel(false);
            }
          }
          if (data) {
            const { aid, status, full_date, clinic_id } = data;
            setSelectedDate(dayjs(full_date.toDate()).startOf("day").toDate());
            setScrollToValues({
              aid,
              queueType:
                status === APPOINTMENT_STATUS.CHECKED_IN
                  ? QUEUE_TYPE.CHECKED_IN
                  : QUEUE_TYPE.BOOKED,
            });

            const currClinicId = useQueueStore.getState().clinicId;
            if (currClinicId !== "all" && currClinicId !== clinic_id) {
              navigateLocal(clinic_id);
              setClinicId(clinic_id);
              getBridges().changeClinic(clinic_id);
            }
          }
          setSelectedPatient(undefined);
          setSelectedPaymode(RECEIPT_PAYMENT_MODE_ID.CASH);
          setSelectedPaymentStatus(undefined);
          setSelectedTags([]);
          setSelectedServiceSkus([]);
          clearSelectedSlot();
          setVcMeta({ platform: SupportedTeleProviders.eka });
        }
        setSelectedInclusionIds([]);
      } finally {
        if (action === "book_appointment") {
          setIsBookAppointmentProcessing(false);
        }
      }
    }
  };

  const showAddMedicalHistoryPopup = useQueueStore(
    (state) => state.showAddMedicalHistoryPopup
  );
  const setShowAddMedicalHistoryPopup = useQueueStore(
    (state) => state.setShowAddMedicalHistoryPopup
  );

  const { data: patientMedicalHistoryApiResponse } = useGetPatientHistory({
    patientOid: selectedPatient?.oid || "",
  });

  const patientMedicalHistory = useMemo(() => {
    if (patientMedicalHistoryApiResponse?.data) {
      return getMedicalHistory({
        data: patientMedicalHistoryApiResponse.data,
      });
    }
    return [];
  }, [patientMedicalHistoryApiResponse?.data]);

  const { data: patientDetails } = useGetPatientByPid({
    patientOid: selectedPatient?.oid || "",
  });

  const wouldConfigComponentRender = React.useCallback(
    (configId: string): boolean => {
      const abha = selectedPatient?.abha || patientDetails?.data?.[0]?.abha;
      switch (configId) {
        case "treatmentPackage":
          return (
            !isTreatmentPlanEnabled() &&
            !!queueConfigData?.data?.packages?.length
          );
        case "abha":
          return !!abha;
        case "medicalHistory":
        case "service":
        case "tags":
          return true;
        case "auto_create_receipt_on_services":
        case "associate_payments_to_appointments":
          return false;
        default:
          return false;
      }
    },
    [
      selectedPatient?.abha,
      patientDetails?.data,
      queueConfigData?.data?.packages,
    ]
  );

  const hasAnyConfigComponentRendered = useMemo(() => {
    if (!createApptConfig || createApptConfig.length === 0) {
      return false;
    }

    const enabledConfigs = createApptConfig.filter((config) => config.isShown);

    if (enabledConfigs.length === 0) {
      return false;
    }

    return enabledConfigs.some((config) =>
      wouldConfigComponentRender(config.id)
    );
  }, [createApptConfig, wouldConfigComponentRender]);

  const renderConfigComponent = (configId: string) => {
    if (!wouldConfigComponentRender(configId)) {
      return null;
    }

    switch (configId) {
      case "treatmentPackage":
        return !isTreatmentPlanEnabled() ? (
          <NewAptSelectExistingPackage />
        ) : null;

      case "abha": {
        const abha = selectedPatient?.abha || patientDetails?.data?.[0]?.abha;
        return abha ? (
          <NewAbhaStatusPatient
            abha={selectedPatient?.abha || patientDetails?.data?.[0]?.abha}
            onClick={() => { }}
          />
        ) : null;
      }

      case "medicalHistory":
        return (
          <Popover
            placement="left"
            open={showAddMedicalHistoryPopup}
            trigger={["click"]}
            onOpenChange={(visible) => {
              setShowAddMedicalHistoryPopup(visible);
            }}
            className="dr-cursor-pointer"
            content={() => (
              <MedicalHistoryPopover patient_id={selectedPatient?.oid || ""} />
            )}
            destroyTooltipOnHide
            overlayClassName="popover-p-0"
          >
            <div>
              <NewAptSelectMedicalHistoryUi
                disabled={!selectedPatient?.oid}
                medicalHistory={patientMedicalHistory}
                setShowAddMedicalHistoryPopup={() => {
                  if (selectedPatient?.oid) {
                    setShowAddMedicalHistoryPopup(true);
                  }
                }}
              />
            </div>
          </Popover>
        );

      case "service":
        return (
          <NewAptSelectedServices
            selectedServiceSkus={selectedServiceSkus}
            setSelectedServiceSkus={setSelectedServiceSkus}
            selectedPaymode={selectedPaymode}
            handleSetSelectedPaymode={setSelectedPaymode}
            clinicId={clinicId}
            paymentStatusMode={isPaymentStatusMode}
            quickReceiptMode={isQuickReceiptMode}
            selectedPaymentStatus={selectedPaymentStatus}
            onPaymentStatusChange={setSelectedPaymentStatus}
          />
        );

      case "tags":
        return (
          <NewAptSelectedTags
            selectedTags={selectedTags}
            handleTagClick={handleTagClick}
          />
        );

      default:
        return null;
    }
  };

  const showOnboardingArrows =
    showArrows &&
    ReactXarrowsWrapper &&
    canShowOnboardingChange &&
    canShowOnboardingArrows &&
    !hasAnyConfigComponentRendered;

  return (
    <div className="dr-flex dr-flex-col dr-h-full dr-relative">
      <div className="dr-h-full dr-overflow-y-scroll">
        <RequestedAppointmentDropdown />
        <NewAptSelectClinic />
        <NewAptSelectDoctor />
        <NewAptSelectedTime />
        <SelectTelePlatform />
        <NewAptSelectedPatient
          selectedPatient={selectedPatient}
          formData={patientDetails?.data?.[0]?.formData}
          showOnboardingArrows={showOnboardingArrows}
        />
        <NewAptExistingAppointment />
        {!createApptConfig ? (
          <div
            className="dr-flex dr-justify-center dr-items-center"
            style={{ minHeight: "100px" }}
          >
            <Spin size="large" />
          </div>
        ) : (
          createApptConfig.map((config) =>
            config.isShown ? (
              <React.Fragment key={config.id}>
                {renderConfigComponent(config.id)}
              </React.Fragment>
            ) : null
          )
        )}

        <div className="dr-flex dr-justify-center pt-12">
          <div
            className="dr-flex dr-items-center dr-space-x-8 dr-px-12 dr-py-8 dr-border dr-border-darwin-touch-neutral-200 dr-bg-text-white dr-rounded-full dr-w-max dr-cursor-pointer"
            onClick={onAdvanceSettingsClick}
          >
            <p>
              <svg
                viewBox="0 0 13 13"
                xmlns="http://www.w3.org/2000/svg"
                className="dr-w-13 dr-h-13"
              >
                <path
                  d="M12.5938 8.02344C12.5938 8.70898 11.2734 11.2988 10.4355 11.2988C10.334 11.2988 10.2324 11.2734 10.1309 11.1973L9.29297 10.7148C9.01367 10.918 8.73438 11.0957 8.42969 11.2227V12.1875C8.42969 12.4668 8.25195 12.7207 7.97266 12.7969C7.41406 12.9492 6.85547 13 6.29688 13C5.71289 13 5.1543 12.9492 4.62109 12.7969C4.3418 12.7207 4.13867 12.4668 4.13867 12.1875V11.2227C3.83398 11.0957 3.55469 10.918 3.27539 10.7148L2.4375 11.1973C2.36133 11.2734 2.23438 11.2988 2.13281 11.2988C1.24414 11.2988 0 8.58203 0 8.02344C0 7.82031 0.101562 7.61719 0.304688 7.49023L1.14258 7.00781C1.11719 6.85547 1.11719 6.67773 1.11719 6.5C1.11719 6.34766 1.11719 6.16992 1.14258 6.01758L0.304688 5.53516C0.101562 5.4082 0 5.20508 0 5.00195C0 4.31641 1.32031 1.72656 2.13281 1.72656C2.23438 1.72656 2.36133 1.75195 2.4375 1.82812L3.27539 2.31055C3.55469 2.10742 3.83398 1.92969 4.13867 1.80273V0.837891C4.13867 0.558594 4.3418 0.304688 4.62109 0.228516C5.1543 0.0761719 5.71289 0 6.29688 0C6.85547 0 7.41406 0.0761719 7.97266 0.228516C8.25195 0.304688 8.42969 0.558594 8.42969 0.837891V1.80273C8.73438 1.92969 9.01367 2.10742 9.29297 2.31055L10.1309 1.82812C10.2324 1.75195 10.334 1.72656 10.4355 1.72656C11.3242 1.72656 12.5938 4.44336 12.5938 5.00195C12.5938 5.20508 12.4668 5.4082 12.2637 5.53516L11.4258 6.01758C11.4512 6.16992 11.4512 6.34766 11.4512 6.5C11.4512 6.67773 11.4512 6.85547 11.4258 7.00781L12.2637 7.49023C12.4668 7.61719 12.5938 7.82031 12.5938 8.02344ZM10.334 9.90234C10.7402 9.44531 11.0449 8.88672 11.248 8.30273C10.7148 7.99805 10.9688 8.15039 10.1562 7.66797C10.207 7.0332 10.2324 6.75391 10.2324 6.5C10.2324 6.24609 10.207 5.99219 10.1562 5.35742C10.9434 4.875 10.7148 5.02734 11.248 4.72266C11.0449 4.13867 10.7402 3.58008 10.334 3.12305C9.80078 3.42773 10.0801 3.25 9.2168 3.75781C8.15039 2.9707 8.4043 3.12305 7.21094 2.58984V1.32031C7.00781 1.26953 6.67773 1.21875 6.29688 1.21875C6.01758 1.21875 5.71289 1.24414 5.35742 1.32031V2.58984C4.16406 3.12305 4.41797 2.9707 3.35156 3.75781C2.53906 3.27539 2.84375 3.45312 2.23438 3.12305C1.85352 3.58008 1.52344 4.13867 1.32031 4.72266C1.85352 5.02734 1.625 4.875 2.4375 5.35742C2.36133 5.99219 2.33594 6.27148 2.33594 6.52539C2.33594 6.7793 2.36133 7.0332 2.4375 7.66797C1.625 8.15039 1.85352 7.99805 1.32031 8.30273C1.52344 8.88672 1.85352 9.44531 2.23438 9.90234C2.76758 9.59766 2.53906 9.75 3.35156 9.26758C4.41797 10.0547 4.16406 9.90234 5.35742 10.4355V11.7051C5.58594 11.7559 5.89062 11.7812 6.29688 11.7812C6.55078 11.7812 6.88086 11.7812 7.21094 11.7051V10.4355C8.4043 9.90234 8.15039 10.0547 9.2168 9.26758C10.0547 9.75 9.72461 9.57227 10.334 9.90234ZM6.29688 4.0625C7.64258 4.0625 8.73438 5.17969 8.73438 6.5C8.73438 7.8457 7.64258 8.9375 6.29688 8.9375C4.95117 8.9375 3.85938 7.8457 3.85938 6.5C3.85938 5.17969 4.95117 4.0625 6.29688 4.0625ZM6.29688 7.71875C6.95703 7.71875 7.51562 7.18555 7.51562 6.5C7.51562 5.83984 6.95703 5.28125 6.29688 5.28125C5.61133 5.28125 5.07812 5.83984 5.07812 6.5C5.07812 7.18555 5.61133 7.71875 6.29688 7.71875Z"
                  fill="#484848"
                />
              </svg>
            </p>
            <p>Advance Settings</p>
            <p>
              <svg
                viewBox="0 0 12 6"
                xmlns="http://www.w3.org/2000/svg"
                className="dr-w-12 dr-h-6 transform -dr-rotate-90"
              >
                <path
                  d="M10.8418 1.11719L5.9668 5.78906C5.83984 5.89062 5.6875 5.94141 5.53516 5.94141C5.4082 5.94141 5.25586 5.89062 5.12891 5.78906L0.253906 1.11719C0 0.888672 0 0.482422 0.228516 0.253906C0.457031 0 0.863281 0 1.0918 0.228516L5.53516 4.49414L10.0039 0.228516C10.2324 0 10.6387 0 10.8672 0.253906C11.0957 0.482422 11.0957 0.888672 10.8418 1.11719Z"
                  fill="#BABABA"
                />
              </svg>
            </p>
          </div>
        </div>

        {showOnboardingArrows && (
          <div className="dr-flex dr-justify-end dr-pt-32">
            <div id={CHECK_IN_TODAYS_FIRST_PATIENT_ID} className="dr-pt-16">
              <img
                src="/images/queue-page/check-in-todays-first-patient.webp"
                alt="check in today's first patient"
                style={{ width: "154px", height: "64px" }}
              />
            </div>
          </div>
        )}

        <style>{`
          .ant-popover-inner {
            border-radius: 1rem;
          }
        `}</style>
        <div className="dr-h-72 dr-min-h-72"></div>
      </div>
      <div className="dr-px-16 dr-absolute dr-w-full dr-bottom-16 xl:dr-bottom-32">
        {isSlotDateFromPast ? (
          <PrimaryButton
            title="Save Appointment"
            onClick={() =>
              handleAddAppointmentClick({ shouldBookAndCheckin: false })
            }
            isLoading={isPostEndVisitLoading}
            isDisabled={
              !selectedPatient ||
              isPostEndVisitLoading ||
              !clinicId ||
              (!queueConfigData?.data?.apt_config.allow_clinic_booking &&
                !selectedDoctor)
            }
            extraClass="dr-w-full"
            paddingLarge="dr-py-12"
            buttonIconPosition="left"
            IconComponent={() => <CalendarSolidIcon />}
          />
        ) : isSelectedDayToday ? (
          <div className="dr-flex dr-space-x-8">
            <div className="dr-flex-1 dr-rounded-8 dr-shadow-elevation-1100">
              <SecondaryButton
                IconComponent={() =>
                  selectedSlot ? (
                    <CalendarSolidIcon />
                  ) : (
                    <PersonWalkingDashedLineArrowRightRegularIcon />
                  )
                }
                title="Book Appointment"
                isDisabled={
                  !selectedPatient ||
                  isPostBookSlotLoading ||
                  isPostCreateReceiptLoading ||
                  isBookAppointmentProcessing ||
                  !clinicId ||
                  (!queueConfigData?.data?.apt_config.allow_clinic_booking &&
                    !selectedDoctor)
                }
                isLoading={
                  activeButton === "book"
                    ? isPostBookSlotLoading ||
                    isPostCreateReceiptLoading ||
                    isBookAppointmentProcessing
                    : false
                }
                onClick={() => {
                  setActiveButton("book");
                  handleAddAppointmentClick({ shouldBookAndCheckin: false });
                }}
                extraClass="dr-w-full"
                paddingLarge="dr-py-12"
                buttonIconPosition="left"
              />
            </div>
            <div
              className={`dr-flex-1 dr-rounded-8 dr-shadow-elevation-1100 ${getAclViewClassName(SUBJECT_KEYS.CHECKIN_APPT)}`}
            >
              <PrimaryButton
                IconComponent={() => (
                  <PersonWalkingDashedLineArrowRightRegularIcon />
                )}
                title="Check-In"
                isDisabled={
                  !selectedPatient ||
                  isPostBookSlotLoading ||
                  isPostCreateReceiptLoading ||
                  !clinicId ||
                  (!queueConfigData?.data?.apt_config.allow_clinic_booking &&
                    !selectedDoctor)
                }
                isLoading={
                  activeButton === "book_and_checkin"
                    ? isPostBookSlotLoading || isPostCreateReceiptLoading
                    : false
                }
                onClick={() => {
                  setActiveButton("book_and_checkin");
                  handleAddAppointmentClick({ shouldBookAndCheckin: true });
                }}
                extraClass="dr-w-full dr-h-full"
                paddingLarge="dr-py-12"
                buttonIconPosition="left"
              />
            </div>
          </div>
        ) : (
          <PrimaryButton
            IconComponent={() =>
              selectedSlot ? (
                <CalendarSolidIcon />
              ) : (
                <PersonWalkingDashedLineArrowRightRegularIcon />
              )
            }
            title={selectedSlot ? "Book Appointment" : "Check-In"}
            isDisabled={
              !selectedPatient ||
              isPostBookSlotLoading ||
              isPostCreateReceiptLoading ||
              (selectedSlot ? isBookAppointmentProcessing : false) ||
              !clinicId ||
              (!queueConfigData?.data?.apt_config.allow_clinic_booking &&
                !selectedDoctor)
            }
            isLoading={
              activeButton === "checkin" || activeButton === "book"
                ? isPostBookSlotLoading ||
                isPostCreateReceiptLoading ||
                (selectedSlot ? isBookAppointmentProcessing : false)
                : false
            }
            onClick={() => {
              setActiveButton("checkin");
              handleAddAppointmentClick({ shouldBookAndCheckin: false });
            }}
            extraClass={`dr-w-full dr-shadow-elevation-1100 ${selectedSlot ? "" : getAclViewClassName(SUBJECT_KEYS.CHECKIN_APPT)}`}
            paddingLarge="dr-py-12"
            buttonIconPosition="left"
          />
        )}
      </div>

      {showOnboardingArrows && (
        <ReactXarrowsWrapper.Xarrow
          start={FREE_LINE_ID}
          end={CHECK_IN_TODAYS_FIRST_PATIENT_ID}
          color="#A852D0"
          strokeWidth={2}
          path="smooth"
          curveness={0.8}
          startAnchor="bottom"
          endAnchor="top"
          animateDrawing={true}
        />
      )}

      <ApptConfigEditModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSave={handleConfigSave}
      />
    </div>
  );
};

export default AddNewPatient;

const MedicalHistoryPopover = ({ patient_id }: { patient_id: string }) => {
  React.useEffect(() => {
    if (!patient_id) return;
    window.renderMHPopup({
      containerId: MH_POPUP_CONTAINER_ID,
      ptOid: patient_id,
    });
  }, [patient_id]);

  if (!patient_id) return <></>;

  return <div id={MH_POPUP_CONTAINER_ID}></div>;
};
